globalThis.ColorDropdownEnhancer = class ColorDropdownEnhancer {
  static registeredEnhancers = new Set();
  static boundDocumentClickHandler = null;

  constructor(rootElement) {
    this.rootElement = rootElement;
    this.instances = [];
    this.activeInstance = null;
    this.handleViewportChange = this.handleViewportChange.bind(this);
  }

  init() {
    const selects = Array.from(this.rootElement.querySelectorAll('.product-form__input--dropdown select'));
    selects.forEach((select) => this.enhanceSelect(select));
    if (this.instances.length > 0) {
      this.register();
    } else {
      this.unregister();
    }
    this.syncAll();
  }

  register() {
    ColorDropdownEnhancer.registeredEnhancers.add(this);
    ColorDropdownEnhancer.ensureDocumentClickListener();
  }

  unregister() {
    ColorDropdownEnhancer.registeredEnhancers.delete(this);
    ColorDropdownEnhancer.cleanupDocumentClickListener();
  }

  teardown() {
    this.closeAll();
    this.unregister();
    this.instances = [];
    this.activeInstance = null;
  }

  static ensureDocumentClickListener() {
    if (ColorDropdownEnhancer.boundDocumentClickHandler) return;
    ColorDropdownEnhancer.boundDocumentClickHandler = (event) => {
      const isInsideEnhancer = event.target.closest('.swatch-select');
      const isInsideOverlayList = event.target.closest('.swatch-select__list');
      if (isInsideEnhancer || isInsideOverlayList) return;

      ColorDropdownEnhancer.registeredEnhancers.forEach((enhancer) => enhancer.closeAll());
    };
    document.addEventListener('click', ColorDropdownEnhancer.boundDocumentClickHandler);
  }

  static cleanupDocumentClickListener() {
    if (
      ColorDropdownEnhancer.registeredEnhancers.size > 0
      || !ColorDropdownEnhancer.boundDocumentClickHandler
    ) {
      return;
    }

    document.removeEventListener('click', ColorDropdownEnhancer.boundDocumentClickHandler);
    ColorDropdownEnhancer.boundDocumentClickHandler = null;
  }

  enhanceSelect(select) {
    if (select.dataset.swatchEnhanced === 'true') return;

    const isModalContext = Boolean(select.closest('.product-modal'));
    const triggerId = `${select.id}-swatch-trigger`;
    const listId = `${select.id}-swatch-listbox`;
    const container = document.createElement('div');
    container.className = 'swatch-select';
    if (isModalContext) {
      container.classList.add('swatch-select--modal');
    }

    const trigger = document.createElement('button');
    trigger.type = 'button';
    trigger.id = triggerId;
    trigger.className = 'swatch-select__trigger';
    trigger.setAttribute('aria-haspopup', 'listbox');
    trigger.setAttribute('aria-expanded', 'false');
    trigger.setAttribute('aria-controls', listId);

    const listbox = document.createElement('div');
    listbox.id = listId;
    listbox.className = 'swatch-select__list';
    listbox.setAttribute('role', 'listbox');
    listbox.setAttribute('tabindex', '-1');
    listbox.hidden = true;

    const options = Array.from(select.options).map((option) => this.buildOptionButton(option, select.id));
    options.forEach((optionButton) => listbox.appendChild(optionButton));

    trigger.addEventListener('click', (event) => {
      event.preventDefault();
      if (listbox.hidden) {
        this.open(select);
      } else {
        this.close(select);
      }
    });

    trigger.addEventListener('keydown', (event) => this.onTriggerKeydown(event, select));
    listbox.addEventListener('keydown', (event) => this.onListboxKeydown(event, select));
    listbox.addEventListener('click', (event) => this.onListboxClick(event, select));
    select.addEventListener('change', () => this.syncSelect(select));

    select.classList.add('swatch-select__native');
    select.ariaHidden = 'true';
    select.tabIndex = -1;

    container.appendChild(trigger);
    container.appendChild(listbox);
    select.after(container);

    select.dataset.swatchEnhanced = 'true';
    this.instances.push({ select, trigger, listbox, container, isModalContext });
  }

  buildOptionButton(option, selectId) {
    const optionButton = document.createElement('button');
    optionButton.type = 'button';
    optionButton.className = 'swatch-select__option';
    optionButton.setAttribute('role', 'option');
    optionButton.dataset.value = option.value;
    optionButton.id = `${selectId}-swatch-option-${option.index}`;
    optionButton.setAttribute('aria-selected', option.selected ? 'true' : 'false');

    const swatch = document.createElement('span');
    swatch.className = 'swatch-select__option-swatch';
    const hasSwatch = this.applySwatchData(swatch, option);

    const label = document.createElement('span');
    label.className = 'swatch-select__option-label';
    label.classList.toggle('style--subdued', this.isOptionUnavailable(option));
    label.textContent = option.textContent.trim();

    if (hasSwatch) optionButton.appendChild(swatch);
    optionButton.appendChild(label);

    return optionButton;
  }

  applySwatchData(swatchElement, option) {
    const swatchImage = option.dataset.swatchImage || option.dataset.swatchFallbackImage;
    const swatchColor = option.dataset.swatchColor;
    swatchElement.style.removeProperty('--swatch-image');
    swatchElement.style.removeProperty('--swatch-color');

    if (swatchImage) {
      swatchElement.style.setProperty('--swatch-image', `url("${swatchImage}")`);
      swatchElement.dataset.swatchKind = 'image';
      return true;
    }

    if (swatchColor) {
      swatchElement.style.setProperty('--swatch-color', swatchColor);
      swatchElement.dataset.swatchKind = 'color';
      return true;
    }

    swatchElement.dataset.swatchKind = 'empty';
    return false;
  }

  isOptionUnavailable(option) {
    return option.dataset.optionDisabled === 'true';
  }

  onListboxClick(event, select) {
    const optionButton = event.target.closest('.swatch-select__option');
    if (!optionButton) return;
    this.selectValue(select, optionButton.dataset.value, true);
    this.close(select);
    this.getInstance(select)?.trigger.focus();
  }

  onTriggerKeydown(event, select) {
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      this.open(select);
      this.focusSelectedOrFirst(select, event.key === 'ArrowUp');
      return;
    }

    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.open(select);
      this.focusSelectedOrFirst(select);
      return;
    }

    if (event.key === 'Escape') {
      this.close(select);
    }
  }

  onListboxKeydown(event, select) {
    const instance = this.getInstance(select);
    if (!instance) return;

    const options = Array.from(instance.listbox.querySelectorAll('.swatch-select__option'));
    const activeIndex = options.indexOf(document.activeElement);

    if (event.key === 'Escape') {
      event.preventDefault();
      this.close(select);
      instance.trigger.focus();
      return;
    }

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      const nextIndex = activeIndex < options.length - 1 ? activeIndex + 1 : 0;
      options[nextIndex]?.focus();
      return;
    }

    if (event.key === 'ArrowUp') {
      event.preventDefault();
      const previousIndex = activeIndex > 0 ? activeIndex - 1 : options.length - 1;
      options[previousIndex]?.focus();
      return;
    }

    if (event.key === 'Home') {
      event.preventDefault();
      options[0]?.focus();
      return;
    }

    if (event.key === 'End') {
      event.preventDefault();
      options.at(-1)?.focus();
      return;
    }

    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      const optionButton = document.activeElement.closest('.swatch-select__option');
      if (!optionButton) return;
      this.selectValue(select, optionButton.dataset.value, true);
      this.close(select);
      instance.trigger.focus();
    }
  }

  selectValue(select, newValue, triggerNativeChange = true) {
    if (select.value === newValue) return;
    select.value = newValue;
    if (triggerNativeChange) {
      select.dispatchEvent(new Event('change', { bubbles: true }));
    } else {
      this.syncSelect(select);
    }
  }

  open(select) {
    this.closeAll();
    const instance = this.getInstance(select);
    if (!instance) return;
    this.activeInstance = instance;
    if (instance.isModalContext) {
      this.mountOverlay(instance);
      this.positionOverlay(instance);
      this.attachViewportListeners();
    }
    instance.listbox.hidden = false;
    instance.trigger.setAttribute('aria-expanded', 'true');
  }

  close(select) {
    const instance = this.getInstance(select);
    if (!instance) return;
    instance.listbox.hidden = true;
    instance.trigger.setAttribute('aria-expanded', 'false');
    if (instance.isModalContext) {
      this.unmountOverlay(instance);
    }
    if (this.activeInstance === instance) {
      this.activeInstance = null;
      this.detachViewportListeners();
    }
  }

  closeAll() {
    this.instances.forEach(({ select }) => this.close(select));
  }

  mountOverlay(instance) {
    const { listbox } = instance;
    if (listbox.parentElement === document.body) return;
    listbox.classList.add('swatch-select__list--overlay');
    document.body.appendChild(listbox);
  }

  unmountOverlay(instance) {
    const { listbox, container } = instance;
    if (listbox.parentElement !== container) {
      container.appendChild(listbox);
    }
    listbox.classList.remove(
      'swatch-select__list--overlay',
      'swatch-select__list--above',
      'swatch-select__list--below'
    );
    listbox.style.removeProperty('top');
    listbox.style.removeProperty('bottom');
    listbox.style.removeProperty('left');
    listbox.style.removeProperty('width');
    listbox.style.removeProperty('max-height');
  }

  attachViewportListeners() {
    document.addEventListener('scroll', this.handleViewportChange, true);
    window.addEventListener('resize', this.handleViewportChange);
  }

  detachViewportListeners() {
    document.removeEventListener('scroll', this.handleViewportChange, true);
    window.removeEventListener('resize', this.handleViewportChange);
  }

  handleViewportChange() {
    if (!this.activeInstance?.isModalContext) return;
    this.positionOverlay(this.activeInstance);
  }

  positionOverlay(instance) {
    const { trigger, listbox } = instance;
    const triggerRect = trigger.getBoundingClientRect();
    const viewportPadding = window.innerWidth < 768 ? 12 : 16;
    const offset = 4;
    const minimumListHeight = 160;
    const availableBelow = window.innerHeight - triggerRect.bottom - offset - viewportPadding;
    const availableAbove = triggerRect.top - offset - viewportPadding;
    const shouldOpenAbove = availableBelow < minimumListHeight && availableAbove > availableBelow;
    const maxHeight = Math.max(
      120,
      Math.min(shouldOpenAbove ? availableAbove : availableBelow, window.innerHeight - viewportPadding * 2)
    );
    const width = Math.min(triggerRect.width, window.innerWidth - viewportPadding * 2);
    const left = Math.min(
      Math.max(triggerRect.left, viewportPadding),
      window.innerWidth - viewportPadding - width
    );

    listbox.classList.toggle('swatch-select__list--above', shouldOpenAbove);
    listbox.classList.toggle('swatch-select__list--below', !shouldOpenAbove);
    listbox.style.left = `${left}px`;
    listbox.style.width = `${width}px`;
    listbox.style.maxHeight = `${Math.floor(maxHeight)}px`;

    if (shouldOpenAbove) {
      listbox.style.top = 'auto';
      listbox.style.bottom = `${Math.max(viewportPadding, window.innerHeight - triggerRect.top + offset)}px`;
      return;
    }

    listbox.style.top = `${Math.max(viewportPadding, triggerRect.bottom + offset)}px`;
    listbox.style.bottom = 'auto';
  }

  focusSelectedOrFirst(select, fromEnd = false) {
    const instance = this.getInstance(select);
    if (!instance) return;

    const options = Array.from(instance.listbox.querySelectorAll('.swatch-select__option'));
    const selected = options.find((option) => option.getAttribute('aria-selected') === 'true');
    if (selected) {
      selected.focus();
      return;
    }

    if (fromEnd) {
      options.at(-1)?.focus();
    } else {
      options[0]?.focus();
    }
  }

  syncSelect(select) {
    const instance = this.getInstance(select);
    if (!instance) return;

    const nativeOptions = Array.from(select.options);
    const selectedOption = select.options[select.selectedIndex];

    const optionButtons = Array.from(instance.listbox.querySelectorAll('.swatch-select__option'));
    optionButtons.forEach((optionButton, index) => {
      const nativeOption = nativeOptions[index];
      if (!nativeOption) return;
      const isSelected = nativeOption.value === select.value;
      optionButton.setAttribute('aria-selected', isSelected ? 'true' : 'false');
      optionButton.classList.toggle('is-selected', isSelected);

      const label = optionButton.querySelector('.swatch-select__option-label');
      if (label) {
        label.textContent = nativeOption.textContent.trim();
        label.classList.toggle('style--subdued', this.isOptionUnavailable(nativeOption));
      }
    });

    if (!selectedOption) return;

    const triggerText = document.createElement('span');
    triggerText.className = 'swatch-select__trigger-label';
    triggerText.textContent = selectedOption.textContent.trim();

    instance.trigger.innerHTML = '';
    const triggerSwatch = document.createElement('span');
    triggerSwatch.className = 'swatch-select__trigger-swatch';
    const hasSwatch = this.applySwatchData(triggerSwatch, selectedOption);
    if (hasSwatch) {
      instance.trigger.classList.remove('swatch-select__trigger--no-swatch');
      instance.trigger.appendChild(triggerSwatch);
    } else {
      instance.trigger.classList.add('swatch-select__trigger--no-swatch');
    }
    instance.trigger.appendChild(triggerText);
  }

  syncAll() {
    this.instances.forEach(({ select }) => this.syncSelect(select));
  }

  getInstance(select) {
    return this.instances.find((instance) => instance.select === select);
  }
};
